import { maunaSpaceAPI } from '@/api';
import { useQuery } from '@tanstack/react-query';
import {
  BadgeCheck,
  CircleCheckBig,
  Gift,
  RefreshCw,
  TicketCheck,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

interface MissionStatistics {
  id: number;
  title: string;
  target: number;
  publish: boolean;
  participants: number;
  inProgress: number;
  completed: number;
  rewardClaimed: number;
  averageProgressRate: number;
  completionRate: number;
  rewardClaimRate: number;
}

interface RewardStatistics {
  id: number;
  title: string;
  issued: number;
  used: number;
  available: number;
  expired: number;
  uniqueRecipients: number;
  usageRate: number;
}

interface MissionRewardStatistics {
  generatedAt: string;
  basis: 'current_snapshot' | 'period_cohort';
  period: {
    startDate: string;
    endDate: string;
  } | null;
  summary: {
    missionParticipations: number;
    missionParticipants: number;
    completedMissions: number;
    completedUsers: number;
    claimedMissionRewards: number;
    rewardClaimedUsers: number;
    missionCompletionRate: number;
    missionRewardClaimRate: number;
    issuedRewards: number;
    rewardRecipients: number;
    usedRewards: number;
    rewardUsers: number;
    availableRewards: number;
    expiredRewards: number;
    rewardUsageRate: number;
  };
  missions: MissionStatistics[];
  rewards: RewardStatistics[];
}

interface MaunaMissionRewardStatisticsProps {
  startDate: string;
  endDate: string;
}

interface RewardRecipient {
  id: number;
  rewardId: number;
  rewardTitle: string;
  nickname: string;
  phone: string | null;
  receivedAt: string;
  isUsed: boolean;
  usedAt: string | null;
}

interface RewardRecipientResult {
  rewardId: number | null;
  startDate: string;
  endDate: string;
  uniqueUsers: number;
  issuedCount: number;
  recipients: RewardRecipient[];
}

interface RewardRecipientSelection {
  id: number | null;
  title: string;
}

const formatNumber = (value: number) => new Intl.NumberFormat('ko-KR').format(value);

const formatGeneratedAt = (value: string) =>
  new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

const formatPhoneNumber = (value: string | null) => {
  if (!value) return '-';

  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return value;
};

const MaunaMissionRewardStatistics = ({
  startDate,
  endDate,
}: MaunaMissionRewardStatisticsProps) => {
  const [selectedReward, setSelectedReward] =
    useState<RewardRecipientSelection | null>(null);
  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['getMissionRewardStatisticsMauna', startDate, endDate],
    queryFn: () =>
      maunaSpaceAPI.getMissionRewardStatistics(
        {
          code: import.meta.env.VITE_SECRET_API_KEY,
        },
        { startDate, endDate }
      ),
    staleTime: 1000 * 60 * 5,
    enabled: Boolean(startDate && endDate),
  });

  const {
    data: recipientData,
    isLoading: isRecipientLoading,
    isError: isRecipientError,
    refetch: refetchRecipients,
  } = useQuery({
    queryKey: [
      'getRewardRecipientsMauna',
      selectedReward?.id,
      startDate,
      endDate,
    ],
    queryFn: () =>
      maunaSpaceAPI.getRewardRecipients(
        {
          code: import.meta.env.VITE_SECRET_API_KEY,
        },
        {
          ...(selectedReward?.id ? { rewardId: selectedReward.id } : {}),
          startDate,
          endDate,
        }
      ),
    staleTime: 1000 * 60,
    enabled: Boolean(selectedReward && startDate && endDate),
  });

  useEffect(() => {
    setSelectedReward(null);
  }, [startDate, endDate]);

  useEffect(() => {
    if (!selectedReward) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedReward(null);
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [selectedReward]);

  const statistics = data?.data?.result as MissionRewardStatistics | undefined;
  const recipientResult = recipientData?.data?.result as
    | RewardRecipientResult
    | undefined;

  if (isLoading) {
    return (
      <DashboardPanel aria-live="polite">
        <StateContent>
          <Spinner />
          <strong>미션·리워드 통계를 집계하고 있습니다.</strong>
          <span>운영 데이터의 현재 상태를 불러오는 중입니다.</span>
        </StateContent>
      </DashboardPanel>
    );
  }

  if (isError || !statistics) {
    return (
      <DashboardPanel>
        <StateContent>
          <strong>미션·리워드 통계를 불러오지 못했습니다.</strong>
          <span>잠시 후 다시 시도해 주세요.</span>
          <RetryButton type="button" onClick={() => refetch()}>
            <RefreshCw size={16} />
            다시 불러오기
          </RetryButton>
        </StateContent>
      </DashboardPanel>
    );
  }

  const { summary } = statistics;
  const activeMissions = statistics.missions.filter((mission) => mission.publish);
  const missionRewardGap = Math.max(
    summary.completedMissions - summary.claimedMissionRewards,
    0
  );
  const rewardStatusTotal =
    summary.usedRewards + summary.availableRewards + summary.expiredRewards;
  const usedWidth = rewardStatusTotal
    ? (summary.usedRewards / rewardStatusTotal) * 100
    : 0;
  const availableWidth = rewardStatusTotal
    ? (summary.availableRewards / rewardStatusTotal) * 100
    : 0;
  const expiredWidth = rewardStatusTotal
    ? (summary.expiredRewards / rewardStatusTotal) * 100
    : 0;

  return (
    <DashboardGroup>
      <DashboardPanel>
        <DashboardHeader>
          <div>
            <Eyebrow>운영 데이터</Eyebrow>
            <DashboardTitle>미션·리워드 성과</DashboardTitle>
            <DashboardDescription>
              선택 기간에 참여를 시작한 공개 미션과 발급된 리워드를 집계합니다.
            </DashboardDescription>
          </div>
          <HeaderActions>
            <UpdatedAt>{formatGeneratedAt(statistics.generatedAt)} 기준</UpdatedAt>
            <RefreshButton
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              title="통계 새로고침"
              aria-label="통계 새로고침"
            >
              <RefreshCw size={18} />
            </RefreshButton>
          </HeaderActions>
        </DashboardHeader>

        <MetricGrid>
          <MetricItem>
            <MetricIcon $tone="blue">
              <Users size={21} />
            </MetricIcon>
            <MetricLabel>미션 참여</MetricLabel>
            <MetricValue>{formatNumber(summary.missionParticipations)}건</MetricValue>
            <MetricMeta>사용자 {formatNumber(summary.missionParticipants)}명</MetricMeta>
          </MetricItem>
          <MetricItem>
            <MetricIcon $tone="green">
              <CircleCheckBig size={21} />
            </MetricIcon>
            <MetricLabel>미션 완료</MetricLabel>
            <MetricValue>{formatNumber(summary.completedMissions)}건</MetricValue>
            <MetricMeta>
              완료율 <strong>{summary.missionCompletionRate}%</strong>
            </MetricMeta>
          </MetricItem>
          <MetricItem>
            <MetricIcon $tone="amber">
              <Gift size={21} />
            </MetricIcon>
            <MetricLabel>리워드 발급</MetricLabel>
            <MetricValue>{formatNumber(summary.issuedRewards)}건</MetricValue>
            <MetricMeta>수령 사용자 {formatNumber(summary.rewardRecipients)}명</MetricMeta>
          </MetricItem>
          <MetricItem>
            <MetricIcon $tone="red">
              <TicketCheck size={21} />
            </MetricIcon>
            <MetricLabel>리워드 사용</MetricLabel>
            <MetricValue>{formatNumber(summary.usedRewards)}건</MetricValue>
            <MetricMeta>
              사용률 <strong>{summary.rewardUsageRate}%</strong>
            </MetricMeta>
          </MetricItem>
        </MetricGrid>
      </DashboardPanel>

      <AnalysisGrid>
        <DashboardPanel>
          <PanelHeader>
            <div>
              <PanelTitle>미션 전환 흐름</PanelTitle>
              <PanelDescription>기간 내 참여를 시작한 공개 미션 기준</PanelDescription>
            </div>
            <StatusBadge $tone={missionRewardGap === 0 ? 'green' : 'amber'}>
              완료 후 미수령 {formatNumber(missionRewardGap)}건
            </StatusBadge>
          </PanelHeader>
          <Funnel>
            <FunnelStep $tone="blue">
              <FunnelLabel>참여</FunnelLabel>
              <FunnelValue>{formatNumber(summary.missionParticipations)}건</FunnelValue>
              <FunnelMeta>{formatNumber(summary.missionParticipants)}명</FunnelMeta>
            </FunnelStep>
            <FunnelConnector aria-hidden="true" />
            <FunnelStep $tone="green">
              <FunnelLabel>완료</FunnelLabel>
              <FunnelValue>{formatNumber(summary.completedMissions)}건</FunnelValue>
              <FunnelMeta>{summary.missionCompletionRate}% 전환</FunnelMeta>
            </FunnelStep>
            <FunnelConnector aria-hidden="true" />
            <FunnelStep $tone="amber">
              <FunnelLabel>리워드 수령</FunnelLabel>
              <FunnelValue>{formatNumber(summary.claimedMissionRewards)}건</FunnelValue>
              <FunnelMeta>완료 대비 {summary.missionRewardClaimRate}%</FunnelMeta>
            </FunnelStep>
          </Funnel>
        </DashboardPanel>

        <DashboardPanel>
          <PanelHeader>
            <div>
              <PanelTitle>리워드 이용 상태</PanelTitle>
              <PanelDescription>기간 내 발급된 쿠폰의 현재 상태</PanelDescription>
            </div>
            <StatusBadge $tone="blue">
              발급 {formatNumber(summary.issuedRewards)}건
            </StatusBadge>
          </PanelHeader>
          <RewardStatusBar aria-label="리워드 사용 상태 비율">
            <RewardStatusSegment $tone="green" style={{ width: `${usedWidth}%` }} />
            <RewardStatusSegment $tone="blue" style={{ width: `${availableWidth}%` }} />
            <RewardStatusSegment $tone="gray" style={{ width: `${expiredWidth}%` }} />
          </RewardStatusBar>
          <StatusLegend>
            <LegendItem>
              <LegendDot $tone="green" />
              <span>사용</span>
              <strong>{formatNumber(summary.usedRewards)}건</strong>
            </LegendItem>
            <LegendItem>
              <LegendDot $tone="blue" />
              <span>사용 가능</span>
              <strong>{formatNumber(summary.availableRewards)}건</strong>
            </LegendItem>
            <LegendItem>
              <LegendDot $tone="gray" />
              <span>기간 만료</span>
              <strong>{formatNumber(summary.expiredRewards)}건</strong>
            </LegendItem>
          </StatusLegend>
        </DashboardPanel>
      </AnalysisGrid>

      <DashboardPanel>
        <PanelHeader>
          <div>
            <PanelTitle>미션별 수행 현황</PanelTitle>
            <PanelDescription>현재 공개된 미션 {activeMissions.length}개</PanelDescription>
          </div>
          <BadgeCheck size={22} color="#1f6feb" aria-hidden="true" />
        </PanelHeader>
        <TableScroll>
          <StatisticsTable>
            <thead>
              <tr>
                <th>미션</th>
                <th>참여</th>
                <th>진행 중</th>
                <th>완료</th>
                <th>리워드 수령</th>
                <th>평균 진행률</th>
                <th>완료율</th>
              </tr>
            </thead>
            <tbody>
              {activeMissions.map((mission) => (
                <tr key={mission.id}>
                  <NameCell>
                    <strong>{mission.title}</strong>
                    <span>목표 {formatNumber(mission.target)}</span>
                  </NameCell>
                  <td>{formatNumber(mission.participants)}건</td>
                  <td>{formatNumber(mission.inProgress)}건</td>
                  <td>{formatNumber(mission.completed)}건</td>
                  <td>{formatNumber(mission.rewardClaimed)}건</td>
                  <RateCell>
                    <RateTrack>
                      <RateFill
                        $tone="blue"
                        style={{ width: `${mission.averageProgressRate}%` }}
                      />
                    </RateTrack>
                    <strong>{mission.averageProgressRate}%</strong>
                  </RateCell>
                  <RateCell>
                    <RateTrack>
                      <RateFill
                        $tone="green"
                        style={{ width: `${mission.completionRate}%` }}
                      />
                    </RateTrack>
                    <strong>{mission.completionRate}%</strong>
                  </RateCell>
                </tr>
              ))}
            </tbody>
          </StatisticsTable>
        </TableScroll>
      </DashboardPanel>

      <DashboardPanel>
        <PanelHeader>
          <div>
            <PanelTitle>리워드별 수령·사용 현황</PanelTitle>
            <PanelDescription>실제 발급 이력이 있는 리워드 기준</PanelDescription>
          </div>
          <Gift size={22} color="#d97706" aria-hidden="true" />
        </PanelHeader>
        <TableScroll>
          <StatisticsTable>
            <thead>
              <tr>
                <th>리워드</th>
                <th>수령</th>
                <th>수령 사용자</th>
                <th>사용</th>
                <th>사용 가능</th>
                <th>기간 만료</th>
                <th>사용률</th>
              </tr>
            </thead>
            <tbody>
              {statistics.rewards.map((reward) => (
                <tr key={reward.id}>
                  <NameCell>
                    <strong>{reward.title}</strong>
                  </NameCell>
                  <td>{formatNumber(reward.issued)}건</td>
                  <td>
                    <RecipientCountButton
                      type="button"
                      onClick={() => setSelectedReward(reward)}
                      title={`${reward.title} 수령 사용자 보기`}
                    >
                      <Users size={14} aria-hidden="true" />
                      {formatNumber(reward.uniqueRecipients)}명
                    </RecipientCountButton>
                  </td>
                  <td>{formatNumber(reward.used)}건</td>
                  <td>{formatNumber(reward.available)}건</td>
                  <td>{formatNumber(reward.expired)}건</td>
                  <RateCell>
                    <RateTrack>
                      <RateFill
                        $tone="amber"
                        style={{ width: `${reward.usageRate}%` }}
                      />
                    </RateTrack>
                    <strong>{reward.usageRate}%</strong>
                  </RateCell>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <TotalRow>
                <NameCell>
                  <strong>Total</strong>
                </NameCell>
                <td>{formatNumber(summary.issuedRewards)}건</td>
                <td>
                  <RecipientCountButton
                    type="button"
                    onClick={() =>
                      setSelectedReward({
                        id: null,
                        title: '전체 리워드 수령 사용자',
                      })
                    }
                    title="전체 리워드 수령 사용자 보기"
                  >
                    <Users size={14} aria-hidden="true" />
                    {formatNumber(summary.rewardRecipients)}명
                  </RecipientCountButton>
                </td>
                <td>{formatNumber(summary.usedRewards)}건</td>
                <td>{formatNumber(summary.availableRewards)}건</td>
                <td>{formatNumber(summary.expiredRewards)}건</td>
                <RateCell>
                  <RateTrack>
                    <RateFill
                      $tone="amber"
                      style={{ width: `${summary.rewardUsageRate}%` }}
                    />
                  </RateTrack>
                  <strong>{summary.rewardUsageRate}%</strong>
                </RateCell>
              </TotalRow>
            </tfoot>
          </StatisticsTable>
        </TableScroll>
      </DashboardPanel>

      <BasisNote>
        미션은 선택 기간에 참여를 시작한 건의 현재 진행값을, 리워드는 해당 기간에
        발급된 건의 현재 사용 상태를 기준으로 집계합니다.
      </BasisNote>

      {selectedReward && (
        <ModalOverlay
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedReward(null);
          }}
        >
          <RecipientModal
            role="dialog"
            aria-modal="true"
            aria-labelledby="reward-recipient-modal-title"
          >
            <ModalHeader>
              <div>
                <ModalEyebrow>수령 사용자</ModalEyebrow>
                <ModalTitle id="reward-recipient-modal-title">
                  {selectedReward.title}
                </ModalTitle>
                <ModalPeriod>
                  {startDate} ~ {endDate}
                </ModalPeriod>
              </div>
              <ModalCloseButton
                type="button"
                onClick={() => setSelectedReward(null)}
                title="모달 닫기"
                aria-label="모달 닫기"
              >
                <X size={20} />
              </ModalCloseButton>
            </ModalHeader>

            {isRecipientLoading ? (
              <RecipientState aria-live="polite">
                <Spinner />
                <strong>수령 사용자 정보를 불러오고 있습니다.</strong>
              </RecipientState>
            ) : isRecipientError || !recipientResult ? (
              <RecipientState>
                <strong>수령 사용자 정보를 불러오지 못했습니다.</strong>
                <RetryButton type="button" onClick={() => refetchRecipients()}>
                  <RefreshCw size={16} />
                  다시 불러오기
                </RetryButton>
              </RecipientState>
            ) : (
              <>
                <ModalSummary>
                  <SummaryItem>
                    <span>수령 사용자</span>
                    <strong>{formatNumber(recipientResult.uniqueUsers)}명</strong>
                  </SummaryItem>
                  <SummaryItem>
                    <span>발급 이력</span>
                    <strong>{formatNumber(recipientResult.issuedCount)}건</strong>
                  </SummaryItem>
                  <ModalSummaryNote>
                    동일 사용자가 여러 차례 수령한 경우 각 수령 건을 따로 표시합니다.
                  </ModalSummaryNote>
                </ModalSummary>
                <RecipientTableScroll>
                  <RecipientTable $showReward={selectedReward.id === null}>
                    <thead>
                      <tr>
                        {selectedReward.id === null && <th>리워드</th>}
                        <th>닉네임</th>
                        <th>전화번호</th>
                        <th>수령 일시</th>
                        <th>사용 여부</th>
                        <th>사용 일시</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipientResult.recipients.map((recipient) => (
                        <tr key={recipient.id}>
                          {selectedReward.id === null && (
                            <td>
                              <RewardName>{recipient.rewardTitle}</RewardName>
                            </td>
                          )}
                          <td>
                            <Nickname>{recipient.nickname}</Nickname>
                          </td>
                          <td>{formatPhoneNumber(recipient.phone)}</td>
                          <td>{formatDateTime(recipient.receivedAt)}</td>
                          <td>
                            <UsageBadge $used={recipient.isUsed}>
                              {recipient.isUsed ? '사용 완료' : '미사용'}
                            </UsageBadge>
                          </td>
                          <td>
                            {recipient.usedAt
                              ? formatDateTime(recipient.usedAt)
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </RecipientTable>
                </RecipientTableScroll>
              </>
            )}
          </RecipientModal>
        </ModalOverlay>
      )}
    </DashboardGroup>
  );
};

export default MaunaMissionRewardStatistics;

const toneColors = {
  blue: '#1f6feb',
  green: '#15803d',
  amber: '#d97706',
  red: '#dc2626',
  gray: '#94a3b8',
};

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const DashboardGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DashboardPanel = styled.section`
  width: 100%;
  padding: 1.25rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
`;

const DashboardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1.1rem;
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const Eyebrow = styled.div`
  margin-bottom: 0.25rem;
  color: #1f6feb;
  font-size: 0.78rem;
  font-weight: 700;
`;

const DashboardTitle = styled.h2`
  margin: 0;
  color: #0f172a;
  font-size: 1.45rem;
  font-weight: 800;
`;

const DashboardDescription = styled.p`
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.88rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const UpdatedAt = styled.span`
  color: #64748b;
  font-size: 0.78rem;
  white-space: nowrap;
`;

const RefreshButton = styled.button`
  display: inline-flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #334155;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #1f6feb;
    background: #eff6ff;
    color: #1f6feb;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.55;
  }
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 1rem;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const MetricItem = styled.div`
  display: grid;
  grid-template-columns: 42px 1fr;
  grid-template-rows: auto auto auto;
  column-gap: 0.75rem;
  min-width: 0;
  padding: 0.8rem 1rem;
  border-right: 1px solid #e2e8f0;

  &:last-child {
    border-right: 0;
  }

  @media (max-width: 960px) {
    &:nth-child(2) {
      border-right: 0;
    }

    &:nth-child(n + 3) {
      border-top: 1px solid #e2e8f0;
    }
  }

  @media (max-width: 540px) {
    border-right: 0;

    &:nth-child(n + 2) {
      border-top: 1px solid #e2e8f0;
    }
  }
`;

const MetricIcon = styled.div<{ $tone: keyof typeof toneColors }>`
  grid-row: 1 / 4;
  display: flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${({ $tone }) => `${toneColors[$tone]}14`};
  color: ${({ $tone }) => toneColors[$tone]};
`;

const MetricLabel = styled.span`
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 600;
`;

const MetricValue = styled.strong`
  margin-top: 0.12rem;
  color: #0f172a;
  font-size: 1.5rem;
  line-height: 1.2;
`;

const MetricMeta = styled.span`
  margin-top: 0.18rem;
  color: #64748b;
  font-size: 0.78rem;

  strong {
    color: #334155;
  }
`;

const AnalysisGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const PanelTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 750;
`;

const PanelDescription = styled.p`
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 0.8rem;
`;

const StatusBadge = styled.span<{ $tone: 'blue' | 'green' | 'amber' }>`
  flex-shrink: 0;
  padding: 0.35rem 0.55rem;
  border-radius: 6px;
  background: ${({ $tone }) => `${toneColors[$tone]}14`};
  color: ${({ $tone }) => toneColors[$tone]};
  font-size: 0.75rem;
  font-weight: 700;
`;

const Funnel = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 34px minmax(0, 1fr) 34px minmax(0, 1fr);
  align-items: stretch;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const FunnelStep = styled.div<{ $tone: 'blue' | 'green' | 'amber' }>`
  min-width: 0;
  padding: 0.85rem;
  border-top: 3px solid ${({ $tone }) => toneColors[$tone]};
  background: #f8fafc;
`;

const FunnelConnector = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 7px;
    width: 20px;
    height: 1px;
    background: #94a3b8;
  }

  &::after {
    content: '';
    position: absolute;
    top: calc(50% - 4px);
    right: 6px;
    width: 7px;
    height: 7px;
    border-top: 1px solid #94a3b8;
    border-right: 1px solid #94a3b8;
    transform: rotate(45deg);
  }

  @media (max-width: 560px) {
    display: none;
  }
`;

const FunnelLabel = styled.span`
  display: block;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
`;

const FunnelValue = styled.strong`
  display: block;
  margin-top: 0.22rem;
  color: #0f172a;
  font-size: 1.25rem;
`;

const FunnelMeta = styled.span`
  display: block;
  margin-top: 0.2rem;
  color: #64748b;
  font-size: 0.75rem;
`;

const RewardStatusBar = styled.div`
  display: flex;
  width: 100%;
  height: 18px;
  overflow: hidden;
  border-radius: 5px;
  background: #e2e8f0;
`;

const RewardStatusSegment = styled.div<{ $tone: 'blue' | 'green' | 'gray' }>`
  height: 100%;
  min-width: ${({ style }) => (style?.width === '0%' ? '0' : '2px')};
  background: ${({ $tone }) => toneColors[$tone]};
`;

const StatusLegend = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const LegendItem = styled.div`
  display: grid;
  grid-template-columns: 9px 1fr;
  align-items: center;
  column-gap: 0.45rem;
  color: #64748b;
  font-size: 0.75rem;

  strong {
    grid-column: 2;
    margin-top: 0.1rem;
    color: #0f172a;
    font-size: 0.95rem;
  }
`;

const LegendDot = styled.span<{ $tone: 'blue' | 'green' | 'gray' }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $tone }) => toneColors[$tone]};
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StatisticsTable = styled.table`
  width: 100%;
  min-width: 880px;
  border-collapse: collapse;
  color: #334155;
  font-size: 0.82rem;

  th,
  td {
    padding: 0.72rem 0.65rem;
    border-bottom: 1px solid #e2e8f0;
    text-align: right;
    vertical-align: middle;
    white-space: nowrap;
  }

  th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.75rem;
    font-weight: 700;
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr:hover {
    background: #f8fafc;
  }

`;

const TotalRow = styled.tr`
  td {
    border-top: 2px solid #94a3b8;
    border-bottom: 0;
    background: #f1f5f9;
    color: #0f172a;
    font-weight: 800;
  }
`;

const RecipientCountButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.32rem 0.48rem;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  background: #eff6ff;
  color: #1d4ed8;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: #1f6feb;
    background: #dbeafe;
  }
`;

const NameCell = styled.td`
  min-width: 220px;
  white-space: normal !important;

  strong {
    display: block;
    color: #0f172a;
    line-height: 1.35;
  }

  span {
    display: block;
    margin-top: 0.12rem;
    color: #94a3b8;
    font-size: 0.72rem;
  }
`;

const RateCell = styled.td`
  min-width: 150px;

  strong {
    display: inline-block;
    width: 44px;
    margin-left: 0.45rem;
    color: #334155;
    text-align: right;
  }
`;

const RateTrack = styled.span`
  display: inline-block;
  width: 72px;
  height: 7px;
  overflow: hidden;
  border-radius: 4px;
  background: #e2e8f0;
  vertical-align: middle;
`;

const RateFill = styled.span<{ $tone: 'blue' | 'green' | 'amber' }>`
  display: block;
  max-width: 100%;
  height: 100%;
  border-radius: inherit;
  background: ${({ $tone }) => toneColors[$tone]};
`;

const BasisNote = styled.p`
  margin: -0.25rem 0 0;
  color: #64748b;
  font-size: 0.75rem;
  line-height: 1.55;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.55);
`;

const RecipientModal = styled.div`
  display: flex;
  width: min(900px, 100%);
  max-height: calc(100vh - 2rem);
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.2rem 1.25rem 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const ModalEyebrow = styled.div`
  margin-bottom: 0.2rem;
  color: #1f6feb;
  font-size: 0.75rem;
  font-weight: 700;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 800;
  line-height: 1.35;
`;

const ModalPeriod = styled.p`
  margin: 0.3rem 0 0;
  color: #64748b;
  font-size: 0.78rem;
`;

const ModalCloseButton = styled.button`
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #334155;
  cursor: pointer;

  &:hover {
    border-color: #94a3b8;
    background: #f1f5f9;
  }
`;

const ModalSummary = styled.div`
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: center;
  gap: 1.4rem;
  padding: 0.85rem 1.25rem;
  background: #f8fafc;

  @media (max-width: 560px) {
    grid-template-columns: 1fr 1fr;
    gap: 0.7rem;
  }
`;

const SummaryItem = styled.div`
  span {
    display: block;
    color: #64748b;
    font-size: 0.72rem;
  }

  strong {
    display: block;
    margin-top: 0.12rem;
    color: #0f172a;
    font-size: 1rem;
  }
`;

const ModalSummaryNote = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.72rem;
  line-height: 1.45;
  text-align: right;

  @media (max-width: 560px) {
    grid-column: 1 / -1;
    text-align: left;
  }
`;

const RecipientTableScroll = styled.div`
  margin: 1rem 1.25rem 1.25rem;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
`;

const RecipientTable = styled.table<{ $showReward: boolean }>`
  width: 100%;
  min-width: ${({ $showReward }) => ($showReward ? '980px' : '800px')};
  border-collapse: collapse;
  color: #334155;
  font-size: 0.8rem;

  th,
  td {
    padding: 0.72rem 0.75rem;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    white-space: nowrap;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
    color: #64748b;
    font-size: 0.74rem;
    font-weight: 700;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr:hover {
    background: #f8fafc;
  }
`;

const RewardName = styled.strong`
  display: block;
  max-width: 220px;
  overflow: hidden;
  color: #334155;
  font-weight: 700;
  text-overflow: ellipsis;
`;

const Nickname = styled.strong`
  color: #0f172a;
  font-weight: 700;
`;

const UsageBadge = styled.span<{ $used: boolean }>`
  display: inline-flex;
  padding: 0.28rem 0.45rem;
  border-radius: 5px;
  background: ${({ $used }) => ($used ? '#dcfce7' : '#f1f5f9')};
  color: ${({ $used }) => ($used ? '#166534' : '#64748b')};
  font-size: 0.72rem;
  font-weight: 700;
`;

const RecipientState = styled.div`
  display: flex;
  min-height: 220px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 1.25rem;
  color: #334155;
  text-align: center;
`;

const StateContent = styled.div`
  display: flex;
  min-height: 150px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  color: #334155;
  text-align: center;

  span {
    color: #64748b;
    font-size: 0.82rem;
  }
`;

const Spinner = styled.span`
  width: 28px;
  height: 28px;
  margin-bottom: 0.35rem;
  border: 3px solid #dbeafe;
  border-top-color: #1f6feb;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.55rem;
  padding: 0.55rem 0.75rem;
  border: 0;
  border-radius: 6px;
  background: #1f6feb;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #1859bd;
  }
`;
